import { EmailTemplate, EmailSettings, VoterProfile } from '../types';

export const TEMPLATE_VARIABLES = [
  { tag: '{{first_name}}', label: 'First Name', description: 'Voter first name (e.g., Adebayo)' },
  { tag: '{{full_name}}', label: 'Full Name', description: 'Full student name (e.g., Adebayo Johnson)' },
  { tag: '{{matric}}', label: 'Matric No.', description: 'Student matric number (e.g., 210001)' },
  { tag: '{{password}}', label: 'Password', description: 'Voter unique authentication pass' },
  { tag: '{{login_url}}', label: 'Voting Link', description: 'Direct election voting link' },
  { tag: '{{election_date}}', label: 'Election Date', description: 'Scheduled date of voting (e.g., 22nd September 2026)' },
  { tag: '{{voting_start_time}}', label: 'Start Time', description: 'Polls opening time (e.g., 8:00 a.m.)' },
  { tag: '{{voting_end_time}}', label: 'End Time', description: 'Polls closing time (e.g., 4:00 p.m.)' },
  { tag: '{{support_email}}', label: 'Support Email', description: 'Support helpdesk email' },
  { tag: '{{election_title}}', label: 'Election Title', description: 'Title of the election' },
];

export function getDefaultTemplates(electionTitle = "LSS UI Election '26"): Record<string, EmailTemplate> {
  return {
    credentials: {
      id: 'credentials',
      name: 'Voter Credentials Pass (Matric + Auto-Password + Direct Link)',
      subject: `[${electionTitle}] Your Official Voting Credentials Pass`,
      body: `Dear {{first_name}},

It's election day! Voting starts at {{voting_start_time}} and ends at {{voting_end_time}} today ({{election_date}}). This is an excellent opportunity to actively engage in the democratic process and make your voice heard.

Below are your login details to access the election platform:

* **Matric Number:** {{matric}}
* **Password:** {{password}}
* **Link:** [{{login_url}}]({{login_url}})

Please use these credentials to log in to the Electpoll platform and cast your vote. Remember, every vote counts towards shaping the future of the faculty.

If you encounter any issues or have any questions, please don't hesitate to contact us at {{support_email}} or an electoral committee member.

Let's make this election a success together!

Best regards,
The Electpoll Team.`,
      updatedAt: new Date().toISOString(),
    },
    live: {
      id: 'live',
      name: '"Election is Live" Announcement',
      subject: `[${electionTitle}] Polls Are Now Officially Open — Cast Your Ballot`,
      body: `Dear {{first_name}},

Polls are officially OPEN! Voting is now live across the faculty from {{voting_start_time}} to {{voting_end_time}} today ({{election_date}}).

Access the ballot directly using your accredited credentials:

* **Matric Number:** {{matric}}
* **Password:** {{password}}
* **Voting Portal:** [{{login_url}}]({{login_url}})

Your voice shapes the future of our faculty. Don't wait until the last minute!

If you encounter any issues or have any questions, please contact {{support_email}}.

Best regards,
The Electpoll Team.`,
      updatedAt: new Date().toISOString(),
    },
    reminder: {
      id: 'reminder',
      name: 'Urgent Final Reminder (Polls Closing Soon)',
      subject: `[URGENT] Polls Closing Soon — Cast Your Vote Before {{voting_end_time}}`,
      body: `Dear {{first_name}},

This is an urgent reminder that polls for {{election_title}} will close at {{voting_end_time}} sharp today ({{election_date}}).

Our records show you have not yet submitted your ballot. Your vote is crucial!

Below are your login details to access the platform:
* **Matric Number:** {{matric}}
* **Password:** {{password}}
* **Voting Portal:** [{{login_url}}]({{login_url}})

If you need immediate assistance, please reach out to {{support_email}}.

Best regards,
The Electpoll Team.`,
      updatedAt: new Date().toISOString(),
    },
    custom: {
      id: 'custom',
      name: 'Custom Announcement Message',
      subject: `[${electionTitle}] Official Electoral Announcement`,
      body: `Dear {{first_name}},

We have an important announcement regarding the ongoing {{election_title}}.

Please ensure you review your ballot choices carefully before submitting your vote.

* **Voting Portal:** [{{login_url}}]({{login_url}})
* **Support Email:** {{support_email}}

Best regards,
The Electpoll Team.`,
      updatedAt: new Date().toISOString(),
    },
  };
}

export function getDefaultSettings(electionTitle = "LSS UI Election '26"): EmailSettings {
  return {
    senderName: "Law Students' Society Electoral Commission",
    replyTo: 'bukola@electpoll.com',
    subjectPrefix: `[${electionTitle}]`,
    loginUrl: 'https://lssui.electpoll.com/',
    supportEmail: 'bukola@electpoll.com',
    votingStartTime: '8:00 a.m.',
    votingEndTime: '4:00 p.m.',
    electionDate: '22nd September 2026',
    activeAuthOption: 'app_password',
    accounts: [
      {
        id: 'acc_1',
        email: 'lss.elections.ui@gmail.com',
        authType: 'app_password',
        appPassword: '•••• •••• •••• ••••',
        dailyLimit: 500,
        sentToday: 0,
        isActive: true,
        status: 'active',
        notes: 'Primary Gmail Dispatch Account',
      },
      {
        id: 'acc_2',
        email: 'lss.electpoll.backup@gmail.com',
        authType: 'app_password',
        appPassword: '•••• •••• •••• ••••',
        dailyLimit: 500,
        sentToday: 0,
        isActive: true,
        status: 'idle',
        notes: 'Secondary Backup (Activates automatically once Account 1 reaches 500)',
      },
    ],
  };
}

export function getSampleVoter(): VoterProfile {
  return {
    matric: '210001',
    name: 'Adebayo Johnson',
    email: 'adebayo.johnson@student.ui.edu.ng',
    level: '300',
    gender: 'Male',
    hall: 'Independence Hall',
    department: 'Public Law',
    password: 'LSS-7X9K2M',
    has_voted: false,
    email_sent_count: 1,
    last_email_sent_at: '2026-09-22T08:05:00Z',
  };
}

export function renderEmailTemplate(
  template: EmailTemplate,
  voter: Partial<VoterProfile>,
  settings: EmailSettings,
  electionTitle = "LSS UI Election '26"
): { subject: string; body: string } {
  const fullName = voter.name || 'Accredited Voter';
  const firstName = fullName.trim().split(' ')[0] || fullName;
  const matric = voter.matric || 'XXXXXX';
  const password = voter.password || '••••••••';
  const loginUrl = settings.loginUrl || 'https://lssui.electpoll.com/';
  const electionDate = settings.electionDate || '22nd September 2026';
  const startTime = settings.votingStartTime || '8:00 a.m.';
  const endTime = settings.votingEndTime || '4:00 p.m.';
  const supportEmail = settings.supportEmail || 'bukola@electpoll.com';

  const replaceMap: Record<string, string> = {
    '{{first_name}}': firstName,
    '{{full_name}}': fullName,
    '{{matric}}': matric,
    '{{password}}': password,
    '{{login_url}}': loginUrl,
    '{{election_date}}': electionDate,
    '{{voting_start_time}}': startTime,
    '{{voting_end_time}}': endTime,
    '{{support_email}}': supportEmail,
    '{{election_title}}': electionTitle,
  };

  let renderedSubject = template.subject;
  let renderedBody = template.body;

  Object.entries(replaceMap).forEach(([key, value]) => {
    const regex = new RegExp(key.replace(/([{}])/g, '\\$1'), 'g');
    renderedSubject = renderedSubject.replace(regex, value);
    renderedBody = renderedBody.replace(regex, value);
  });

  return {
    subject: renderedSubject,
    body: renderedBody,
  };
}
